import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMessage } from 'devextreme/localization';
import { Column } from 'devextreme-react/data-grid';
import { Button, DateBox, Template, TextBox } from 'devextreme-react';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { RequiredRule, Validator } from 'devextreme-react/validator';
import DataGridMVP from '../../DataGridMVP';
import columnDataGrid from './columnDataGrid';
import style from './contractsadmin.module.scss';
import PopupMVP from '../../PopupMVP';

const initialSelectedData = {
  contractNumberSys: '',
  contractNumberLog: '',
  contractNumId: '',
  contractDate: '',
  selected: false
};

const initialAddData = {
  contractNumberSys: '',
  contractNumberLog: '',
  contractNumId: '',
  contractDate: new Date()
};
const ContractsAdmin = ({
  selectedIndex,
  stockReducer: {
    selectPlantId: plantId
  },
  contractReducer: {
    contractsByStock,
    loading,
    contractDelete,
    contractAdd,
    contractUpdate
  },
  getContractsByStock,
  deleteContract,
  addContract,
  updateContract
}) => {
  const [selectedContract, setSelectedContract] = useState(initialSelectedData);
  const [dataSource, setDataSource] = useState(initialAddData);
  const [visibleAddContractModal, setVisibleAddContractModal] = useState(false);
  const [visibleDeleteContractModal, setVisibleDeleteContractModal] = useState(false);
  const [visibleUpdateContractModal, setVisibleUpdateContractModal] = useState(false);

  /** Получение списка договоров */
  const getContractsList = useCallback(() => {
    getContractsByStock({ plantId });
  }, [getContractsByStock, plantId]);

  useEffect(() => {
    if (selectedIndex === 6) {
      getContractsList();
    }
  }, [getContractsList, selectedIndex]);

  const onSelectContract = useCallback(({ selectedRowsData }) => {
    if (selectedRowsData.length) {
      setSelectedContract((prevState) => (
        {
          ...prevState,
          contractNumId: selectedRowsData[0].contractNumId || initialSelectedData.contractNumId,
          contractNumberLog:
            selectedRowsData[0].contractNumberLog
            || initialSelectedData.contractNumberLog,
          contractNumberSys:
            selectedRowsData[0].contractNumberSys
            || initialSelectedData.contractNumberSys,
          contractDate: selectedRowsData[0].contractDate || initialSelectedData.contractDate,
          selected: true
        }
      ));
    }
  }, []);

  const onHidingAddContractModal = () => {
    setVisibleAddContractModal(false);
  };
  const onHidingDeleteContractModal = () => {
    setVisibleDeleteContractModal(false);
  };
  const onHidingUpdateContractModal = () => {
    setVisibleUpdateContractModal(false);
  };

  /** Отправка данных о новом договоре */
  const onAddContract = useCallback(() => {
    addContract({ ...dataSource, plantId });
  }, [addContract, dataSource, plantId]);

  /** Отправка данных об измененном договоре */
  const onUpdateContract = useCallback(() => {
    updateContract({ ...selectedContract, plantId });
  }, [updateContract, selectedContract, plantId]);

  useEffect(() => {
    if (contractDelete.okRequest) {
      setSelectedContract(initialSelectedData);
      getContractsList();
      onHidingDeleteContractModal();
    }
  }, [contractDelete.okRequest, getContractsList]);

  useEffect(() => {
    if (contractAdd.okRequest) {
      getContractsList();
      onHidingAddContractModal();
    }
  }, [contractAdd.okRequest, getContractsList]);

  useEffect(() => {
    if (contractUpdate.okRequest) {
      getContractsList();
      onHidingUpdateContractModal();
    }
  }, [contractUpdate.okRequest, getContractsList]);

  /** Модальное окно редактирования договора */
  const renderModalUpdateContract = useMemo(() => (
    <PopupMVP
      visible={visibleUpdateContractModal}
      onHiding={onHidingUpdateContractModal}
      title={formatMessage('adminPanel').textUpdateContractTitle}
    >
      <div className="formData">
        <div className="optionsRow">
          <div className="optionsData">
            <span>{formatMessage('adminPanel').contractNumberLog}</span>
            <TextBox stylingMode="outlined"
                     value={selectedContract.contractNumberLog}
                     onValueChanged={({ value }) => {
                       setSelectedContract((prevState) => ({
                         ...prevState,
                         contractNumberLog: value
                       }));
                     }}
                     isValid={!!selectedContract.contractNumberLog}
            >
              <Validator>
                <RequiredRule message="Обязательно для ввода" />
              </Validator>
            </TextBox>
          </div>
          <div className="optionsData">
            <span>{formatMessage('adminPanel').contractNumberSys}</span>
            <TextBox stylingMode="outlined"
                     value={selectedContract.contractNumberSys}
                     onValueChanged={({ value }) => {
                       setSelectedContract((prevState) => ({
                         ...prevState,
                         contractNumberSys: value
                       }));
                     }}
                     isValid={!!selectedContract.contractNumberSys}
            >
              <Validator>
                <RequiredRule message="Обязательно для ввода" />
              </Validator>
            </TextBox>
          </div>
        </div>
        <div className="optionsRow">
          <div className="optionsData" style={{ flex: '0 50%' }}>
            <span>{formatMessage('adminPanel').contractDate}</span>
            <DateBox type="date"
                     displayFormat="dd.MM.yyyy"
                     value={selectedContract.contractDate}
                     onValueChanged={({ value }) => setSelectedContract((prevState) => ({
                       ...prevState,
                       contractDate: value
                     }))}
                     width="100%"
                     stylingMode="outlined"
                     openOnFieldClick
                     useMaskBehavior
                     isValid={!!selectedContract.contractDate}
            >
              <Validator>
                <RequiredRule message="Обязательно для ввода" />
              </Validator>
            </DateBox>
          </div>
        </div>
        <div className="formButton">
          <Button text={formatMessage('textButton').cancel}
                  type="default"
                  stylingMode="text"
                  onClick={onHidingUpdateContractModal}
          />
          <Button type="default"
                  stylingMode="contained"
                  onClick={onUpdateContract}
                  width={200}
                  disabled={
                    !selectedContract.contractNumId
                    || !selectedContract.contractNumberSys
                    || !selectedContract.contractNumberLog
                    || !selectedContract.contractDate
                  }
          >
            <LoadIndicator className="indicator" visible={contractUpdate.loading} />
            <span>{formatMessage('textButton').edit}</span>
          </Button>
        </div>
      </div>
    </PopupMVP>
  ), [
    selectedContract,
    visibleUpdateContractModal,
    contractUpdate.loading,
    onUpdateContract
  ]);

  /** Модальное окно добавления нового договора */
  const renderModalAddContract = useMemo(() => (
    <PopupMVP visible={visibleAddContractModal}
              onHiding={onHidingAddContractModal}
              title={formatMessage('adminPanel').textAddContractTitle}
    >
      <div className="formData">
        <div className="optionsRow">
          <div className="optionsData">
            <span>{formatMessage('adminPanel').contractNumberLog}</span>
            <TextBox stylingMode="outlined"
                     value={dataSource.contractNumberLog}
                     onValueChanged={({ value }) => {
                       setDataSource((prevState) => ({
                         ...prevState,
                         contractNumberLog: value
                       }));
                     }}
                     isValid={!!dataSource.contractNumberLog}
            >
              <Validator>
                <RequiredRule message="Обязательно для ввода" />
              </Validator>
            </TextBox>
          </div>
          <div className="optionsData">
            <span>{formatMessage('adminPanel').contractNumberSys}</span>
            <TextBox stylingMode="outlined"
                     value={dataSource.contractNumberSys}
                     onValueChanged={({ value }) => {
                       setDataSource((prevState) => ({
                         ...prevState,
                         contractNumberSys: value
                       }));
                     }}
                     isValid={!!dataSource.contractNumberSys}
            >
              <Validator>
                <RequiredRule message="Обязательно для ввода" />
              </Validator>
            </TextBox>
          </div>
        </div>
        <div className="optionsRow">
          <div className="optionsData" style={{ flex: '0 50%' }}>
            <span>{formatMessage('adminPanel').contractDate}</span>
            <DateBox type="date"
                     displayFormat="dd.MM.yyyy"
                     value={dataSource.contractDate}
                     onValueChanged={({ value }) => setDataSource((prevState) => ({
                       ...prevState,
                       contractDate: value
                     }))}
                     width="100%"
                     stylingMode="outlined"
                     openOnFieldClick
                     useMaskBehavior
                     isValid={!!dataSource.contractDate}
            >
              <Validator>
                <RequiredRule message="Обязательно для ввода" />
              </Validator>
            </DateBox>
          </div>
        </div>
        <div className="formButton">
          <Button text={formatMessage('textButton').cancel}
                  type="default"
                  stylingMode="text"
                  onClick={onHidingAddContractModal}
          />
          <Button type="default"
                  stylingMode="contained"
                  onClick={onAddContract}
                  width={200}
                  disabled={!dataSource.contractNumberSys
                    || !dataSource.contractNumberLog
                    || !dataSource.contractDate}
          >
            <LoadIndicator className="indicator" visible={contractAdd.loading} />
            <span>{formatMessage('textButton').add}</span>
          </Button>
        </div>
      </div>
    </PopupMVP>
  ), [contractAdd.loading, dataSource, visibleAddContractModal, onAddContract]);

  /** Модальное окно удаления договора */
  const renderDeleteContractModal = useMemo(() => (
    <PopupMVP visible={visibleDeleteContractModal}
              onHiding={onHidingDeleteContractModal}
              title={formatMessage('textModal').textDeleteContractTitle}
    >
      <div className="formData">
        <span>
          {`${formatMessage('adminPanel').textDeleteContract} №${selectedContract.contractNumId}?`}
        </span>
        <div className="formButton">
          <Button text={formatMessage('textButton').cancel}
                  type="default"
                  stylingMode="text"
                  onClick={onHidingDeleteContractModal}
          />
          <Button type="default"
                  stylingMode="contained"
                  onClick={() => { deleteContract({ docNumId: selectedContract.contractNumId }); }}
                  width={200}
          >
            <LoadIndicator className="indicator" visible={contractDelete.loading} />
            <span>{formatMessage('textButton').remove}</span>
          </Button>
        </div>
      </div>
    </PopupMVP>
  ), [
    contractDelete.loading,
    deleteContract,
    selectedContract.contractNumId,
    visibleDeleteContractModal
  ]);
  const onOpenAddContractModal = () => {
    setVisibleAddContractModal(true);
    setDataSource(initialAddData);
  };

  /** Рендер кнопки добавления договора */
  const toolbarButtonAddContract = useMemo(() => () => (
    <Button type="default"
            stylingMode="contained"
            icon="plus"
            text={formatMessage('textButton').add}
            elementAttr={{ class: style.buttonAdd }}
            onClick={onOpenAddContractModal}
    />
  ), []);

  /** Рендер кнопки удаления договора */
  const toolbarButtonDeleteContract = useMemo(() => () => (
    <Button
      text={formatMessage('textButton').remove}
      type="danger"
      stylingMode="contained"
      onClick={() => setVisibleDeleteContractModal(true)}
      disabled={!selectedContract.selected}
    />
  ), [selectedContract.selected]);

  const toolbarButtonUpdateContract = useMemo(() => () => (
    <Button
      text={formatMessage('textButton').edit}
      type="default"
      stylingMode="contained"
      onClick={() => setVisibleUpdateContractModal(true)}
      disabled={!selectedContract.selected}
    />
  ), [selectedContract.selected]);

  return (
    <>
      <DataGridMVP className="shipment"
                 keyExpr="contractNumId"
                 setDataGridInstance={() => {}}
                 visibleButtonDateRangePicker={false}
                 visibleSummary={false}
                 visibleGroupPanel={false}
                 visibleInfoIcon={false}
                 visibleFilter={false}
                 visibleFilterPanel={false}
                 visibleHeaderFilter={false}
                 visibleSearchPanel={false}
                 visibleButtonAddResponsiblePerson
                 visibleButtonDeleteContract
                 visibleButtonUpdateContract
                 dataSource={contractsByStock}
                 selectionMode="single"
                 onSelectionChanged={onSelectContract}
                 loading={loading}
    >
        {columnDataGrid.map((item) => (
          <Column
          dataField={item.dataField}
          key={item.dataField}
          caption={formatMessage('adminPanel')[item.dataField] || item.caption}
          visible
          allowFiltering
          width={item.width}
          cellRender={item.cellRender}
          dataType={item.dataType}
          allowSearch={!item.notAllowSearch}
          sortOrder={item.sortOrder}
         />
        ))}
        <Template name="toolbarButtonAddResponsiblePerson" render={toolbarButtonAddContract} />
        <Template name="toolbarButtonDeleteContract" render={toolbarButtonDeleteContract} />
        <Template name="toolbarButtonUpdateContract" render={toolbarButtonUpdateContract} />
      </DataGridMVP>
      {renderModalAddContract}
      {renderDeleteContractModal}
      {renderModalUpdateContract}
    </>
  );
};

export default React.memo(ContractsAdmin);
